import { SeeqNames } from '@seeqdev/constants';
import { faBarChart, faLineChart, faSortAmountAsc, faSortAmountDesc } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcCapsuleSet, fcScalar, fcSeries } from '@seeqdev/icons/seeq';
import type { IconDefinition } from '@seeqdev/icons';

export enum OutputType {
  CONDITION = 'CONDITION',
  SIGNAL = 'SIGNAL',
}

export enum OutputSignalType {
  LOGLIKE = 'loglike_signal',
  T2 = 't2_signal',
  SPE = 'spe_signal',
}

const packageName = SeeqNames.MlService.PrincipalComponentAnalysis.Connection.UdfPackage;
const udfName = SeeqNames.MlService.PrincipalComponentAnalysis.Connection.UdfName;
export const UDF_NAME = `${packageName}_${udfName}`;

export const LOADINGS_KEY = 'PCA loadings';
export const MODEL_INPUT_PROPERTIES = 'model_input_properties';
export const SORT_AS_STRING = true;
export const EXPLAINED_VARIANCE_KEY = 'Explained variance ratio';
export const CUMULATIVE_R_SQUARED = 'Cumulative R-squared';
export const DEFAULT_NUM_COMPONENTS = 3;
export const DEFAULT_ANOMALY_THRESHOLD = 99;
export const DEFAULT_MONITORING_CONTRAST = 0.5;
export const VALIDATION_ERROR_DELAY = 500;
export const MIN_NUM_COMPONENTS = 1;
export const MAX_MONITORING_CONTRAST = 5;
export const MAX_THRESHOLD = 100;
export const SCORE_OUTPUT = 'scoreOutput';
export const MAX_INPUT_SIGNALS = 50;

export const SORT_TYPES = [
  {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.ASCENDING',
    icon: faSortAmountAsc,
    option: { ascending: true, absolute: false },
  },
  {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.ABSOLUTE_ASCENDING',
    icon: faSortAmountAsc,
    option: { ascending: true, absolute: true },
  },
  {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.DESCENDING',
    icon: faSortAmountDesc,
    option: { ascending: false, absolute: false },
  },
  {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.ABSOLUTE_DESCENDING',
    icon: faSortAmountDesc,
    option: { ascending: false, absolute: true },
  },
];

export const CHART_TYPES: { key: string; label: string; icon: IconDefinition; iconExtraClassNames?: string }[] = [
  {
    key: 'bar',
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.BAR',
    icon: faBarChart,
  },
  {
    key: 'column',
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.COLUMN',
    icon: faBarChart,
    iconExtraClassNames: 'fa-rotate-270',
  },
  {
    key: 'line',
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.CHART.LINE',
    icon: faLineChart,
  },
];

export const INPUT_NAMES = {
  nComponentsInput: 'nComponentsInput',
  anomalyThresholdInput: 'anomalyThresholdInput',
  monitoringSignalContrastInput: 'monitoringSignalContrastInput',
  mergeCapsuleDurationInput: 'mergeCapsuleDurationInput',
  minCapsuleDurationInput: 'minCapsuleDurationInput',
  startTimeInput: 'startTimeInput',
  endTimeInput: 'endTimeInput',
  conditionNameInput: 'conditionNameInput',
  loglikeSignalNameInput: 'loglikeSignalNameInput',
  t2SignalNameInput: 't2SignalNameInput',
  speSignalNameInput: 'speSignalNameInput',
  signalInput: 'signalInput',
  restrictOutputToConditionInput: 'restrictOutputToConditionInput',
  trainWithinConditionInput: 'trainWithinConditionInput',
};

export const OUTPUT_TRANSLATION_KEYS: Record<string, { label: string; icon: IconDefinition }> = {
  'anomalyThresholdOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.ANOMALY_THRESHOLD',
    icon: fcScalar,
  },
  'anomalyOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.ANOMALY_CONDITION',
    icon: fcCapsuleSet,
  },
  'totalExplainedVarianceOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.TOTAL_EXPLAINED_VARIANCE',
    icon: fcScalar,
  },
  'modelInputPropertiesOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.MODEL_INPUT_PROPERTIES',
    icon: fcScalar,
  },
  'loglikeSignalOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.LOG_LIKELIHOOD_SIGNAL',
    icon: fcSeries,
  },
  't2SignalOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.T2_SIGNAL',
    icon: fcSeries,
  },
  'speSignalOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.SPE_SIGNAL',
    icon: fcSeries,
  },
  'numberOfComponentsOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.NUMBER_OF_PRINCIPAL_COMPONENTS',
    icon: fcScalar,
  },
  'hotellingT2LimitOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.HOTELLING_T2_LIMIT',
    icon: fcScalar,
  },
  'speLimitOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.SPE_LIMIT',
    icon: fcScalar,
  },
  'pcaLoadingsOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.PCA_LOADINGS',
    icon: fcScalar,
  },
  'cumulativeR-squaredOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.CUMULATIVE_R_SQUARED',
    icon: fcScalar,
  },
  'loglikeLimitOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.LOG_LIKELIHOOD_LIMIT',
    icon: fcScalar,
  },
  'explainedVarianceRatioOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.EXPLAINED_VARIANCE_RATIO',
    icon: fcScalar,
  },
  'meanSquareErrorOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.MEAN_SQUARED_ERROR',
    icon: fcScalar,
  },
  'monitoringSignalContrastOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.MONITORING_SIGNAL_CONTRAST',
    icon: fcScalar,
  },
  'trainingWindowConditionOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.TRAINING_WINDOW_CONDITION',
    icon: fcCapsuleSet,
  },
  'eigenvaluesOutput': {
    label: 'INVESTIGATE_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V2.OUTPUTS.EIGENVALUES',
    icon: fcScalar,
  },
};

export const DEFAULT_OUTPUTS: string[] = ['anomalyOutput', 'loglikeSignalOutput', 't2SignalOutput', 'speSignalOutput'];

export const MODEL_NAME = SeeqNames.ModelNames.PrincipalComponentAnalysis;
